import {
  ASC,
  DataGridLayout,
  DropDownFilter,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  useListContext,
} from 'framework/list';
import { EnumDropdown } from 'framework/form';
import { BusinessFunctionFilter } from 'domain/shared/businessFunction';
import { TechnologiesFilter } from 'domain/shared/career';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import { BadgeField, EnumField, TextField } from 'framework/ui/field';
import PositionSlotMarkedForDeletionBadge from '../field/PositionSlotMarkedForDeletionBadge';
import PositionSlotMultipleEmployeesBadge from '../field/PositionSlotMultipleEmployeesBadge';
import PositionSlotPendingConfirmationBadge from '../field/PositionSlotPendingConfirmationBadge';
import PositionSlotCategoryField from '../field/PositionSlotCategoryField';
import PositionSlotWorkerVacancyField from '../field/PositionSlotWorkerVacancyField';
import usePositionSlotsService from '../usePositionSlotsService';
import {
  DEFAULT_POSITION_SLOT_STATUS_FILTER,
  POSITION_SLOT_STATUS,
  POSITION_SLOT_STATUS_LABELS,
  POSITION_SLOT_WORKER_TYPE_LABELS,
} from '../constants';
import SpecializationFilter from '../filter/SpecializationFilter';
import PositionSlotStatusFilter from '../filter/PositionSlotStatusFilter';
import PositionSlotOfficeFilter from '../filter/PositionSlotOfficeFilter';
import CreatePositionSlotModalButton from '../buttons/CreatePositionSlotModalButton';
import PositionSlotActionsButton from '../buttons/PositionSlotActionsButton';
import { PositionSlotValidationProvider } from '../PositionSlotValidationContext';

const PositionSlotsListActions = () => {
  const { reloadData = () => {} } = useListContext();

  return <CreatePositionSlotModalButton onSuccess={() => reloadData()} />;
};

const PositionSlotsListPage = () => {
  const { getPositionSlotsListDataMethod } = usePositionSlotsService();

  const filters = (
    <Filters>
      <FiltersLayout>
        <SpecializationFilter source="specializationId" label="Specialization" />
        <TechnologiesFilter source="technologyId" label="Technology" />
        <BusinessFunctionFilter source="businessFunctionId" label="Business function" />
        <DropDownFilter source="competencyLevel" label="Competency level">
          <EnumDropdown source="competencyLevel" options={EMPLOYEE_COMPETENCY_LEVEL} />
        </DropDownFilter>
        <PositionSlotStatusFilter source="status" label="Status" />
        <DropDownFilter source="workerType" label="Category">
          <EnumDropdown source="workerType" options={POSITION_SLOT_WORKER_TYPE_LABELS} />
        </DropDownFilter>
        <PositionSlotOfficeFilter source="specification:positionSlotByOfficeSpecification" label="Location" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <List
      getDataMethod={getPositionSlotsListDataMethod}
      defaultSortOrder={ASC}
      defaultSortField="specialization"
      defaultFilter={{
        status: {
          in: DEFAULT_POSITION_SLOT_STATUS_FILTER,
        },
      }}
      pageSize={100}
    >
      <PositionSlotValidationProvider>
        <ListPageLayout
          header={<ListHeader label="Positions" icon="fa-briefcase" />}
          filter={filters}
          actions={[<PositionSlotsListActions key="create-position-slot" />]}
          sxPaper={{ p: '10px !important' }}
        >
          <DataGridLayout buttons={[<PositionSlotActionsButton key="position-slot-actions" />]}>
            <BadgeField
              badges={[
                <PositionSlotPendingConfirmationBadge
                  id="pending-confirmation"
                  isVisible={({ status }: { status: string }) => status === POSITION_SLOT_STATUS.PENDING}
                />,
                <PositionSlotMarkedForDeletionBadge
                  id="marked-for-archival"
                  isVisible={({ isMarkedForDeletion }: { isMarkedForDeletion: boolean }) => isMarkedForDeletion}
                />,
                <PositionSlotMultipleEmployeesBadge
                  id="multiple-employees-assigned"
                  isVisible={({ employeeAliases = [] }: { employeeAliases?: string[] }) => employeeAliases.length > 1}
                />,
              ]}
              source="specializationLabel"
              label="Position"
              isSortable
              sortSource="specialization"
              width={160}
            >
              <TextField source="specializationLabel" />
            </BadgeField>
            <TextField source="technologyLabel" label="Technology" width={120} />
            <TextField source="competencyLevel" label="Level" isSortable width={80} />
            <TextField source="businessFunctionLabel" label="Business function" width={150} />
            <PositionSlotCategoryField source="workerType" label="Category" isSortable width={120} />
            <TextField source="officesLabel" label="Location" width={120} />
            <EnumField source="status" label="Status" options={POSITION_SLOT_STATUS_LABELS} isSortable width={100} />
            <PositionSlotWorkerVacancyField source="employeeAlias" label="Worker / Vacancy" width={200} />
          </DataGridLayout>
        </ListPageLayout>
      </PositionSlotValidationProvider>
    </List>
  );
};

export default PositionSlotsListPage;
